import React, { useState, useEffect, useCallback } from 'react';
import { Mail, Pencil, Trash2, Stethoscope, CheckCircle, XCircle } from 'lucide-react';
import { Btn, Modal, FormField, Badge, inputCls } from '../ui';
import { useToast } from '../../contexts/NotificationContext';
import {
  getMedicalVisitEmailTemplates,
  saveMedicalVisitEmailTemplate,
  deleteMedicalVisitEmailTemplate,
} from '../../api';

const VARIABLES = [
  { key: '{{prenom}}', desc: 'Prénom de l\'employé' },
  { key: '{{nom}}', desc: 'Nom de l\'employé' },
  { key: '{{type_visite}}', desc: 'Type de visite (ex : Visite périodique)' },
  { key: '{{date_visite}}', desc: 'Date de la visite' },
  { key: '{{date_prochaine_visite}}', desc: 'Date de prochaine visite (ou —)' },
  { key: '{{structure}}', desc: 'Nom de la structure' },
];

const EMPTY_FORM = { subject: '', body: '' };

export default function HRMedicalVisitEmailTemplateManagement() {
  const toast = useToast();
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingType, setEditingType] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await getMedicalVisitEmailTemplates();
      setTypes(data);
    } catch {
      toast.error('Erreur chargement templates email');
    }
    setLoading(false);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);

  const openEdit = (type) => {
    setEditingType(type);
    setForm(type.emailTemplate
      ? { subject: type.emailTemplate.subject, body: type.emailTemplate.body }
      : EMPTY_FORM
    );
  };

  const insertVar = (key) => {
    setForm(f => ({ ...f, body: f.body + key }));
  };

  const handleSave = async () => {
    if (!form.subject.trim()) { toast.error("L'objet du mail est requis"); return; }
    if (!form.body.trim()) { toast.error('Le corps du mail est requis'); return; }
    setSaving(true);
    try {
      await saveMedicalVisitEmailTemplate({
        typeId: editingType.id,
        subject: form.subject,
        body: form.body,
      });
      toast.success('Template enregistré');
      setEditingType(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Erreur enregistrement');
    }
    setSaving(false);
  };

  const handleDelete = async (type) => {
    try {
      await deleteMedicalVisitEmailTemplate(type.id);
      toast.success('Template supprimé');
      load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Erreur suppression');
    }
    setDeleteConfirm(null);
  };

  return (
    <div className="px-4 sm:px-6 lg:px-8 space-y-6">
      <div className="flex items-center gap-2">
        <Mail size={18} className="text-primary" />
        <h2 className="text-lg font-semibold text-gray-800">Templates mail — Visites médicales</h2>
      </div>

      <p className="text-sm text-gray-500">
        Configurez un template d'email de relance par type de visite médicale. Le cron quotidien
        (8h00) envoie automatiquement le mail aux employés dont la prochaine visite est dans
        moins de 30 jours et n'ont pas encore reçu de relance. Vous pouvez aussi envoyer une
        relance manuellement depuis la fiche employé.
      </p>

      <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 text-sm text-amber-800">
        <strong>Prérequis :</strong> le SMTP de la structure doit être configuré dans{' '}
        <em>Admin &gt; Identité de structure &gt; Email & SMTP</em>. L'employé doit avoir une adresse email sur sa fiche.
      </div>

      {loading ? (
        <div className="py-12 text-center text-sm text-gray-400">Chargement…</div>
      ) : types.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 py-16 flex flex-col items-center gap-3 text-center">
          <Mail size={32} className="text-gray-300" />
          <p className="text-sm font-medium text-gray-500">Aucun type de visite médicale configuré</p>
          <p className="text-xs text-gray-400 max-w-xs">
            Créez d'abord des types de visite dans <em>Admin &gt; Types de visites médicales</em>,
            puis revenez ici pour configurer les templates d'email.
          </p>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-100">
              <tr>
                <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-500">Type de visite</th>
                <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-500">Template</th>
                <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-500">Objet</th>
                <th className="px-4 py-2.5 text-right text-xs font-medium text-gray-500">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {types.map((t) => (
                <tr key={t.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 font-medium text-gray-800">
                    {t.name}
                    {!t.isActive && <span className="ml-2 text-xs text-gray-400">(inactif)</span>}
                  </td>
                  <td className="px-4 py-3">
                    {t.emailTemplate
                      ? <Badge color="bg-green-100 text-green-700"><CheckCircle size={11} className="inline mr-1" />Configuré</Badge>
                      : <Badge color="bg-gray-100 text-gray-400"><XCircle size={11} className="inline mr-1" />Non configuré</Badge>}
                  </td>
                  <td className="px-4 py-3 text-gray-500 max-w-xs truncate">
                    {t.emailTemplate?.subject || <span className="text-gray-300">—</span>}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex justify-end gap-1">
                      <Btn variant="ghost" size="sm" onClick={() => openEdit(t)}>
                        <Pencil size={13} /> {t.emailTemplate ? 'Modifier' : 'Configurer'}
                      </Btn>
                      {t.emailTemplate && (
                        <Btn variant="ghost" size="sm" onClick={() => setDeleteConfirm(t)} className="text-red-500 hover:text-red-600">
                          <Trash2 size={13} />
                        </Btn>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Modal édition template */}
      {editingType && (
        <Modal
          title={`Template — ${editingType.name}`}
          size="lg"
          onClose={() => setEditingType(null)}
          footer={
            <>
              <Btn variant="secondary" size="sm" onClick={() => setEditingType(null)}>Annuler</Btn>
              <Btn variant="primary" size="sm" loading={saving} onClick={handleSave}>Enregistrer</Btn>
            </>
          }
        >
          <div className="space-y-4">
            <FormField label="Objet du mail" required>
              <input
                className={inputCls}
                value={form.subject}
                onChange={e => setForm(f => ({ ...f, subject: e.target.value }))}
                placeholder="Ex : Rappel — Visite médicale à planifier pour {{prenom}} {{nom}}"
              />
            </FormField>

            <div>
              <p className="text-xs font-medium text-gray-500 mb-2">Variables disponibles (cliquez pour insérer dans le corps)</p>
              <div className="flex flex-wrap gap-1.5 mb-3">
                {VARIABLES.map(v => (
                  <button
                    key={v.key}
                    type="button"
                    onClick={() => insertVar(v.key)}
                    title={v.desc}
                    className="px-2 py-0.5 rounded bg-primary/10 text-primary text-xs font-mono hover:bg-primary/20 transition-colors"
                  >
                    {v.key}
                  </button>
                ))}
              </div>
            </div>

            <FormField label="Corps du mail" required>
              <textarea
                className={inputCls}
                rows={10}
                value={form.body}
                onChange={e => setForm(f => ({ ...f, body: e.target.value }))}
                placeholder={`Bonjour {{prenom}} {{nom}},\n\nNous vous rappelons que votre {{type_visite}} est prévue le {{date_prochaine_visite}}.\n\nMerci de confirmer votre disponibilité.\n\nCordialement,\n{{structure}}`}
              />
              <p className="text-xs text-gray-400 mt-1">Les retours à la ligne sont conservés. Vous pouvez utiliser du HTML basique si besoin.</p>
            </FormField>
          </div>
        </Modal>
      )}

      {/* Modal suppression */}
      {deleteConfirm && (
        <Modal
          title="Supprimer le template"
          size="sm"
          onClose={() => setDeleteConfirm(null)}
          footer={
            <>
              <Btn variant="secondary" size="sm" onClick={() => setDeleteConfirm(null)}>Annuler</Btn>
              <Btn variant="danger" size="sm" onClick={() => handleDelete(deleteConfirm)}>Supprimer</Btn>
            </>
          }
        >
          <p className="text-sm text-gray-700">
            Supprimer le template de <strong>{deleteConfirm.name}</strong> ? Les relances automatiques pour ce type de visite seront désactivées.
          </p>
        </Modal>
      )}
    </div>
  );
}
